import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import type { AddUserSchema } from "@/modules/admin/users"
import { UserIdentityFields, addUserSchema } from "@/modules/admin/users"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { Form } from "@/nzds/forms/form/form"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import { useCreateUser } from "@/services/user"
import { useUrl } from "@/utils/url"

import { Link as MuiLink, Stack } from "@mui/material"

const defaultValues: Partial<AddUserSchema> = {
  firstName: "",
  lastName: "",
  email: "",
}

export const AdminUserNewRoute: React.FC = () => {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminUsersPath } = useUrl()

  const form = useForm({
    schema: addUserSchema,
    defaultValues,
    mode: "onChange",
  })

  const navigateToList = () => {
    void navigate(adminUsersPath, { replace: true })
  }

  const { createUserMutateAsync, createUserIsPending } = useCreateUser({
    onSuccess: () => {
      navigateToList()
    },
  })

  const handleSubmit: SubmitHandler<AddUserSchema> = async (values) => {
    await createUserMutateAsync({
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      role: values.role,
      organization_id: activeOrganizationId
        ? Number(activeOrganizationId)
        : undefined,
    })
  }

  const handleCancel = () => {
    if (createUserIsPending) {
      return
    }

    navigateToList()
  }

  if (!activeOrganizationId) {
    return null
  }

  return (
    <Form<AddUserSchema>
      aria-label={t("addUser.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <MuiLink component={Link} to={adminUsersPath} underline="hover">
              {t("admin.users")}
            </MuiLink>
          }
          heading={t("addUser.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={createUserIsPending}
                onClick={handleCancel}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton disabled={createUserIsPending}>
                {t("button.save")}
              </SubmitButton>
            </>
          }
        />
        <PageCard>
          <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
            <UserIdentityFields disabled={createUserIsPending} />
          </Stack>
        </PageCard>
      </Stack>
    </Form>
  )
}
